import React, { useMemo, useState } from 'react';
import { ArrowUpDown, Download, Inbox } from 'lucide-react';
import type { DemandaEsteira } from '../../../types';
import { FILAS, formatBRL, formatDia } from './filas';
import { donoEfetivo, ehDaRegua, ehDeDocumento } from './normalizar';
import UcsDoCard from './UcsDoCard';

interface Props {
    demandas: DemandaEsteira[];
    onAbrir: (d: DemandaEsteira) => void;
    /** Clique no NOME abre o detalhe (UC ou negociação), não a lista filtrada. */
    onAbrirDetalhe: (d: DemandaEsteira) => void;
}

type Coluna = 'cliente' | 'fila' | 'dias' | 'dono' | 'valor' | 'trava';

/**
 * Quem está segurando a demanda, em texto.
 *
 * No distrato vem da lista nominal de pendentes; nas outras filas o pendente é
 * implícito (o procurador, o cliente) e a coluna diz isso em vez de ficar vazia.
 */
function quemTrava(d: DemandaEsteira): string {
    const bruto = d.bruto as { assinantes_pendentes?: Array<{ nome?: string | null; email?: string | null; interno?: boolean }> } | undefined;
    const pendentes = Array.isArray(bruto?.assinantes_pendentes) ? bruto!.assinantes_pendentes! : [];
    if (pendentes.length) {
        const internos = pendentes.filter(p => p.interno);
        const alvo = internos.length ? internos : pendentes;
        const nomes = alvo.map(p => (p.nome?.trim() || p.email?.split('@')[0] || '?').split(' ')[0]);
        return `${internos.length ? 'Vigor' : 'Cliente'}: ${nomes.join(', ')}`;
    }
    if (d.fila === 'mora_sem_assinatura' || d.fila === 'advertencia_sem_assinatura') return 'Vigor: procurador';
    if (d.fila === 'mora_sem_envio' || d.fila === 'advertencia_sem_envio') return 'Vigor: envio';
    if (d.fila === 'sem_responsavel') return '— ninguém assumiu';
    return 'Cliente';
}

const csvSeguro = (v: unknown) => {
    const s = String(v ?? '');
    // Sempre entre aspas: nome de cliente com vírgula é a regra, não a exceção.
    return `"${s.replace(/"/g, '""')}"`;
};

/**
 * Modo Tabela — densidade e exportação.
 *
 * Existe por dois motivos medidos: a lista em cards mostra ~4 demandas por tela
 * contra ~25 aqui, o que torna triagem de 1.117 linhas inviável em cards; e a
 * gerente exportou planilha de faturas 17 vezes em 12 dias, sinal de que a
 * resposta que ela procura era montada fora do sistema. O export daqui sai com
 * o recorte que já está filtrado na tela.
 */
const TabelaDemandas: React.FC<Props> = ({ demandas, onAbrir, onAbrirDetalhe }) => {
    const [ordem, setOrdem] = useState<{ col: Coluna; desc: boolean }>({ col: 'dias', desc: true });

    const linhas = useMemo(() => {
        const valor = (d: DemandaEsteira): string | number => {
            switch (ordem.col) {
                case 'cliente': return (d.cliente_nome || '').toLowerCase();
                case 'fila': return FILAS[d.fila].titulo;
                case 'dias': return d.dias_parado;
                case 'dono': return (d.atribuicao?.responsavel_nome || d.responsavel_nome || '').toLowerCase();
                case 'valor': return d.valor ?? -1;
                case 'trava': return quemTrava(d).toLowerCase();
            }
        };
        return [...demandas].sort((a, b) => {
            const va = valor(a), vb = valor(b);
            const cmp = typeof va === 'number' && typeof vb === 'number'
                ? va - vb
                : String(va).localeCompare(String(vb), 'pt-BR');
            return ordem.desc ? -cmp : cmp;
        });
    }, [demandas, ordem]);

    const exportar = () => {
        const cab = ['Cliente', 'CPF/CNPJ', 'UC', 'Fila', 'Dias parado', 'Prazo', 'Dono', 'Quem trava', 'Valor'];
        const corpo = linhas.map(d => [
            d.cliente_nome, d.cpf_cnpj_cliente, (d.ucs ?? []).map(u => u.numero).join(', ') || d.uc_numero, FILAS[d.fila].titulo,
            d.dias_parado, d.prazo_em ? formatDia(d.prazo_em) : '',
            d.atribuicao?.responsavel_nome || d.responsavel_nome || '',
            quemTrava(d), d.valor ?? '',
        ].map(csvSeguro).join(';'));

        // BOM + ";" porque o destino é o Excel em pt-BR: sem os dois, acento
        // quebra e tudo cai numa coluna só.
        const blob = new Blob(['﻿' + [cab.map(csvSeguro).join(';'), ...corpo].join('\n')], {
            type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `painel-cobranca-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const Cabecalho = ({ col, children, alinhar }: { col: Coluna; children: React.ReactNode; alinhar?: 'right' }) => (
        <th
            className={`px-3 py-2 font-bold text-gray-500 dark:text-gray-400 whitespace-nowrap ${alinhar === 'right' ? 'text-right' : 'text-left'}`}
            aria-sort={ordem.col === col ? (ordem.desc ? 'descending' : 'ascending') : 'none'}
        >
            <button
                onClick={() => setOrdem(o => ({ col, desc: o.col === col ? !o.desc : true }))}
                className={`inline-flex items-center gap-1 hover:text-gray-800 dark:hover:text-gray-200 ${ordem.col === col ? 'text-primary' : ''}`}
            >
                {children}
                <ArrowUpDown size={11} className={ordem.col === col ? '' : 'opacity-30'} />
            </button>
        </th>
    );

    if (!linhas.length) {
        return (
            <div className="p-12 text-center bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Nada para listar com este recorte.</p>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    {linhas.length} demanda{linhas.length > 1 ? 's' : ''} — clique numa linha para trabalhar.
                </p>
                <button
                    onClick={exportar}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5"
                    title="Baixa exatamente o que está filtrado, já aberto no Excel"
                >
                    <Download size={13} /> Exportar
                </button>
            </div>

            <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
                        <tr>
                            {/* Fixa a coluna de Cliente ao rolar na horizontal — sem
                                isto, no celular a pessoa rola e perde de vista de quem
                                é a linha que está olhando. */}
                            <th
                                className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-900/40 px-3 py-2 font-bold text-gray-500 dark:text-gray-400 whitespace-nowrap text-left"
                                aria-sort={ordem.col === 'cliente' ? (ordem.desc ? 'descending' : 'ascending') : 'none'}
                            >
                                <button
                                    onClick={() => setOrdem(o => ({ col: 'cliente', desc: o.col === 'cliente' ? !o.desc : true }))}
                                    className={`inline-flex items-center gap-1 hover:text-gray-800 dark:hover:text-gray-200 ${ordem.col === 'cliente' ? 'text-primary' : ''}`}
                                >
                                    Cliente
                                    <ArrowUpDown size={11} className={ordem.col === 'cliente' ? '' : 'opacity-30'} />
                                </button>
                            </th>
                            <Cabecalho col="fila">Fila</Cabecalho>
                            <Cabecalho col="dias">Parado</Cabecalho>
                            <Cabecalho col="trava">Quem trava</Cabecalho>
                            <Cabecalho col="dono">Dono</Cabecalho>
                            <Cabecalho col="valor" alinhar="right">Valor</Cabecalho>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-gray-700/60">
                        {linhas.map(d => {
                            const meta = FILAS[d.fila];
                            const dono = d.atribuicao?.responsavel_nome || (ehDaRegua(d) ? d.responsavel_nome : null);
                            return (
                                <tr
                                    key={d.id}
                                    onClick={() => onAbrir(d)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAbrir(d); }
                                    }}
                                    tabIndex={0}
                                    role="button"
                                    aria-label={`Abrir na Lista: ${d.cliente_nome || 'cliente sem nome'}`}
                                    className="group hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-inset"
                                >
                                    <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700/40 px-3 py-2 max-w-[240px]">
                                        {/* O nome abre o detalhe; o resto da linha
                                            leva para a Lista. Duas ações no mesmo
                                            lugar exigem parar a propagação. */}
                                        <button
                                            onClick={e => { e.stopPropagation(); onAbrirDetalhe(d); }}
                                            // Sem isto o Enter no nome subia para a linha, que dá preventDefault
                                            // e abre a Lista: o teclado nunca chegava ao detalhe.
                                            onKeyDown={e => e.stopPropagation()}
                                            className="font-semibold text-gray-900 dark:text-white truncate max-w-full text-left hover:text-primary hover:underline underline-offset-2"
                                            title={ehDeDocumento(d) && d.uc_numero
                                                ? `Abrir a UC ${d.uc_numero}`
                                                : 'Abrir a negociação de cobrança'}
                                        >
                                            {d.cliente_nome || 'Cliente sem nome'}
                                        </button>
                                        {/* Sem truncate: com CNPJ + UC de 12 dígitos + "+N" a linha passa
                                            dos ~216px da célula e o corte comia o "+N" ou parte do número
                                            da UC. Quebra de linha mantém tudo legível e clicável. */}
                                        <p className="text-[10px] text-gray-400 dark:text-gray-500">
                                            {d.cpf_cnpj_cliente || null}
                                            <UcsDoCard d={d} separador={!!d.cpf_cnpj_cliente} />
                                            {!d.cpf_cnpj_cliente && !d.uc_numero && !d.ucs?.length ? '—' : null}
                                        </p>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${meta.badge}`}>
                                            {meta.titulo}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        <span className={`font-bold ${
                                            d.dias_parado > 15 ? 'text-red-600 dark:text-red-400'
                                                : d.dias_parado > 7 ? 'text-amber-600 dark:text-amber-400'
                                                    : 'text-gray-500 dark:text-gray-400'
                                        }`}>
                                            {d.dias_parado <= 0 ? 'hoje' : `${d.dias_parado}d`}
                                        </span>
                                        {d.prazo_em ? (
                                            <span className="text-[10px] text-gray-400 block">prazo {formatDia(d.prazo_em)}</span>
                                        ) : null}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300 max-w-[180px] truncate">
                                        {quemTrava(d)}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        {dono ? (
                                            <span className="text-gray-600 dark:text-gray-300">{dono}</span>
                                        ) : (
                                            <span className="text-amber-600 dark:text-amber-400 font-semibold">sem dono</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-gray-700 dark:text-gray-200">
                                        {d.valor != null && d.valor > 0 ? formatBRL(d.valor) : '—'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TabelaDemandas;
